/**
 * Serves the tenant config the BFF resolves from the request Host, hydrates
 * instantly from sessionStorage on repeat visits (kills the unbranded flash),
 * and applies title/favicon/theme.
 */
import type { StorefrontFeatureKey } from "@loot-os/api-contract";
import * as Sentry from "@sentry/react";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { lazy, type ReactNode, Suspense, useEffect, useMemo } from "react";

import { analyticsOf, configureAnalytics } from "../lib/analytics.js";
import { qk } from "../lib/queries.js";
import {
  type TenantConfig,
  TenantContext,
  type TenantContextValue,
  takeConfigCacheBypass,
} from "../lib/tenant.js";
import { termlyOf } from "../lib/termly.js";
import { applyCookieBannerTheme, applyPageTheme } from "../lib/theme.js";

/** Loot Labs' own policy, read only on the bare platform root: its own chunk, kept off every store. */
const PlatformPrivacyPage = lazy(() =>
  import("../pages/PlatformPrivacyPage.js").then((module) => ({
    default: module.PlatformPrivacyPage,
  })),
);

/** Fail-closed backstop for config from an older BFF missing newer keys. */
const DEFAULT_FEATURES: Record<StorefrontFeatureKey, boolean> = {
  balancePurchases: false,
  battles: false,
  chat: false,
  forge: false,
};

const CACHE_KEY = `sf-config:${window.location.host}`;

/**
 * The cached config, or null when there is none or it predates a field every
 * render dereferences: a config saved before the currency existed would otherwise
 * crash first paint on `config.currency.multiplier`.
 */
function readCache(): TenantConfig | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TenantConfig>;
    const current =
      typeof parsed.currency?.multiplier === "number" &&
      Array.isArray(parsed.shipping?.destinations) &&
      typeof parsed.legal?.supportEmail === "string" &&
      typeof parsed.forge?.commissionEnabled === "boolean";
    return current ? (parsed as TenantConfig) : null;
  } catch {
    return null;
  }
}

function writeCache(config: TenantConfig | null): void {
  try {
    if (config) sessionStorage.setItem(CACHE_KEY, JSON.stringify(config));
    else sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // storage unavailable (private mode) — first paint just stays neutral
  }
}

function applyChrome(config: TenantConfig): void {
  document.title = config.name;
  applyPageTheme(config.theme);
  applyCookieBannerTheme(termlyOf(config).theme);
  const faviconUrl = config.branding.faviconUrl;
  if (faviconUrl && /^https?:\/\//.test(faviconUrl)) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }
}

// Paint the cached brand at import time — that beats first paint, and can't double-run.
const bootConfig = readCache();
if (bootConfig) applyChrome(bootConfig);

// 404 comes back as data, not a throw: it *replaces* the cached config of a deleted store.
// `platformRoot` is the BFF saying the host is the storefront root itself, where no store lives.
type TenantResult =
  | { kind: "ok"; config: TenantConfig }
  | { kind: "not_found"; platformRoot: boolean };

const tenantQueryOptions = queryOptions({
  queryKey: qk.tenant,
  queryFn: async (): Promise<TenantResult> => {
    const res = await fetch("/api/storefront/config", {
      headers: { accept: "application/json" },
      cache: takeConfigCacheBypass() ? "no-cache" : "default",
    });
    if (res.status === 404) {
      writeCache(null);
      const body = (await res.json().catch(() => null)) as { platformRoot?: unknown } | null;
      return { kind: "not_found", platformRoot: body?.platformRoot === true };
    }
    if (!res.ok) throw new Error(`config fetch failed: ${res.status}`);
    const body = (await res.json()) as { config: TenantConfig };
    writeCache(body.config);
    return { kind: "ok", config: body.config };
  },
  // Paint from sessionStorage, revalidate once per page load; on failure v5 keeps `data`.
  initialData: bootConfig ? { kind: "ok" as const, config: bootConfig } : undefined,
  refetchOnMount: "always",
  staleTime: Infinity,
  gcTime: Infinity,
  retry: 1,
});

/** Provides the store's config to the app; mounted once, above everything else. */
export function TenantConfigProvider({ children }: { children?: ReactNode }) {
  const query = useQuery(tenantQueryOptions);
  const config = query.data?.kind === "ok" ? query.data.config : null;

  // A DOM side effect on the query data, never part of the fetch; applyChrome is idempotent.
  useEffect(() => {
    if (!config) return;
    applyChrome(config);
    Sentry.setTag("tenant_id", config.tenantId);
    Sentry.setTag("tenant_slug", config.slug);
  }, [config]);

  // The analytics mode is applied from the fresh config only: a sessionStorage-hydrated config
  // from an older BFF has no `analytics` block, and reading that as anything would open or close
  // the gate on stale grounds. Until the fetch answers, nothing is recorded.
  const fetchedAfterMount = query.isFetchedAfterMount;
  useEffect(() => {
    if (!config || !fetchedAfterMount) return;
    configureAnalytics(analyticsOf(config));
  }, [config, fetchedAfterMount]);

  const value = useMemo<TenantContextValue | null>(
    () =>
      config
        ? {
            config,
            hasFeature: (key) => config.features?.[key] ?? DEFAULT_FEATURES[key],
            configFresh: fetchedAfterMount,
          }
        : null,
    [config, fetchedAfterMount],
  );

  if (value) {
    return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
  }
  if (query.data?.kind === "not_found") {
    // The root has no store and no router; its one page is Loot Labs' own privacy policy.
    if (query.data.platformRoot && /^\/privacy\/?$/.test(window.location.pathname)) {
      return (
        <Suspense fallback={null}>
          <PlatformPrivacyPage />
        </Suspense>
      );
    }
    return (
      <FullPageMessage title="Store not found">
        There's no storefront at <span className="font-mono">{window.location.host}</span>.
      </FullPageMessage>
    );
  }
  if (query.isPending) return <Splash />;
  return (
    <FullPageMessage title="Something went wrong">
      We couldn't load this store.{" "}
      <button type="button" className="underline" onClick={() => void query.refetch()}>
        Try again
      </button>
    </FullPageMessage>
  );
}

/** Neutral (unbranded) first paint while the config loads. */
function Splash() {
  return <div className="min-h-svh bg-bg-canvas" aria-busy="true" />;
}

function FullPageMessage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-h-svh items-center justify-center bg-bg-canvas px-4 text-text-primary">
      <div className="max-w-sm text-center">
        <h1 className="font-body text-heading-h1">{title}</h1>
        <p className="mt-2 font-body text-body-b2 text-text-secondary">{children}</p>
      </div>
    </div>
  );
}
